export interface NavigationChunk {
  readonly id: string;
  readonly districtName: string;
  readonly x: number;
  readonly z: number;
  readonly halfX: number;
  readonly halfZ: number;
  readonly active: boolean;
}

export interface NavigationSnapshot {
  readonly district: string;
  readonly chunks: readonly NavigationChunk[];
  readonly player: {
    readonly x: number;
    readonly z: number;
    readonly heading: number;
  };
  readonly objective: {
    readonly x: number;
    readonly z: number;
    readonly label: string;
    readonly distance: number;
  };
}

export interface MinimapPoint {
  readonly x: number;
  readonly y: number;
}

export interface MinimapBounds {
  readonly minX: number;
  readonly maxX: number;
  readonly minZ: number;
  readonly maxZ: number;
}

export const MINIMAP = {
  width: 196,
  height: 118,
  padding: 12,
} as const;

export function minimapBounds(chunks: readonly NavigationChunk[]): MinimapBounds {
  if (chunks.length === 0) return { minX: -48, maxX: 240, minZ: -22, maxZ: 32 };
  return chunks.reduce<MinimapBounds>((bounds, chunk) => ({
    minX: Math.min(bounds.minX, chunk.x - chunk.halfX),
    maxX: Math.max(bounds.maxX, chunk.x + chunk.halfX),
    minZ: Math.min(bounds.minZ, chunk.z - chunk.halfZ),
    maxZ: Math.max(bounds.maxZ, chunk.z + chunk.halfZ),
  }), {
    minX: Number.POSITIVE_INFINITY,
    maxX: Number.NEGATIVE_INFINITY,
    minZ: Number.POSITIVE_INFINITY,
    maxZ: Number.NEGATIVE_INFINITY,
  });
}

export function projectToMinimap(x: number, z: number, bounds: MinimapBounds): MinimapPoint {
  return {
    x: mapClamped(x, bounds.minX, bounds.maxX, MINIMAP.padding, MINIMAP.width - MINIMAP.padding),
    y: mapClamped(z, bounds.minZ, bounds.maxZ, MINIMAP.padding, MINIMAP.height - MINIMAP.padding),
  };
}

export function renderMinimapSvg(snapshot: NavigationSnapshot): string {
  const bounds = minimapBounds(snapshot.chunks);
  const player = projectToMinimap(snapshot.player.x, snapshot.player.z, bounds);
  const objective = projectToMinimap(snapshot.objective.x, snapshot.objective.z, bounds);
  const heading = headingPoint(player, snapshot.player.heading, 8);
  const routeY = projectToMinimap(snapshot.player.x, Math.max(snapshot.player.z, -1), bounds).y;
  const label = escapeText(snapshot.objective.label);
  const distance = `${Math.round(snapshot.objective.distance)}m`;

  return `
    <svg class="na-hud-map-svg" viewBox="0 0 ${MINIMAP.width} ${MINIMAP.height}" role="img" aria-label="City map">
      <rect class="na-hud-map-bg" x="0" y="0" width="${MINIMAP.width}" height="${MINIMAP.height}" />
      ${snapshot.chunks.map((chunk) => renderChunk(chunk, bounds)).join('')}
      <rect class="na-hud-map-water" x="${MINIMAP.padding}" y="${projectToMinimap(0, 14, bounds).y}" width="${MINIMAP.width - MINIMAP.padding * 2}" height="20" rx="1" />
      <rect class="na-hud-map-road" x="${MINIMAP.padding}" y="${routeY - 7}" width="${MINIMAP.width - MINIMAP.padding * 2}" height="14" rx="1" />
      <line class="na-hud-map-route" x1="${player.x}" y1="${routeY}" x2="${objective.x}" y2="${routeY}" />
      <text class="na-hud-map-label na-hud-map-safehouse-label" x="${objective.x - 21}" y="${Math.max(12, objective.y - 9)}">${label}</text>
      <text class="na-hud-map-distance" x="${objective.x - 9}" y="${Math.min(MINIMAP.height - 8, objective.y + 17)}">${distance}</text>
      <circle class="na-hud-map-objective" cx="${objective.x}" cy="${objective.y}" r="4.5" />
      <circle class="na-hud-map-player" cx="${player.x}" cy="${player.y}" r="4" />
      <line class="na-hud-map-heading" x1="${player.x}" y1="${player.y}" x2="${heading.x}" y2="${heading.y}" />
    </svg>
  `;
}

function renderChunk(chunk: NavigationChunk, bounds: MinimapBounds): string {
  const topLeft = projectToMinimap(chunk.x - chunk.halfX, chunk.z - chunk.halfZ, bounds);
  const bottomRight = projectToMinimap(chunk.x + chunk.halfX, chunk.z + chunk.halfZ, bounds);
  const width = bottomRight.x - topLeft.x;
  const height = bottomRight.y - topLeft.y;
  const labelX = topLeft.x + Math.max(4, width * 0.06);
  const labelY = topLeft.y + 13;
  const activeClass = chunk.active ? ' is-active' : '';
  return `
    <rect class="na-hud-map-chunk${activeClass}" x="${topLeft.x}" y="${topLeft.y}" width="${width}" height="${height}" rx="1" />
    <text class="na-hud-map-label" x="${labelX}" y="${labelY}">${escapeText(chunk.districtName)}</text>
  `;
}

function headingPoint(origin: MinimapPoint, heading: number, length: number): MinimapPoint {
  return {
    x: origin.x + Math.sin(heading) * length,
    y: origin.y + Math.cos(heading) * length,
  };
}

function mapClamped(value: number, inMin: number, inMax: number, outMin: number, outMax: number): number {
  const t = Math.max(0, Math.min(1, (value - inMin) / (inMax - inMin)));
  return outMin + (outMax - outMin) * t;
}

function escapeText(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}
